// src/context/AuthContext.js  — FIXED
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';

// Mirror of backend ROLE_PERMISSIONS — fallback if backend omits permissions
const ROLE_PERMISSIONS = {
  admin: [
    'dashboard', 'products', 'fridges', 'assign_products',
    'stock_monitor', 'sales_report', 'refill_schedule',
    'orders', 'customers',
  ],
  staff: [
    'dashboard', 'stock_monitor', 'refill_schedule', 'orders',
  ],
};

function permissionsFor(role) {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['staff'];
}

// ─── Helper: build a clean admin object from any API response ───────────────
function buildAdmin(adminData, token) {
  const role = adminData.role || 'staff';
  const permissions =
    Array.isArray(adminData.permissions) && adminData.permissions.length > 0
      ? adminData.permissions
      : permissionsFor(role);           // fallback — never let permissions be []
  return { ...adminData, role, permissions, token };
}

export const AuthProvider = ({ children }) => {
  const [admin,   setAdmin]   = useState(null);
  const [loading, setLoading] = useState(true);  // true until first auth check done

  // ── Restore session on mount ─────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setLoading(false);
      return;
    }

    fetch(`${API}/admin/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type':  'application/json',
      },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (data.success && data.admin) {
          setAdmin(buildAdmin(data.admin, token));
        } else {
          localStorage.removeItem('adminToken');
          setAdmin(null);
        }
      })
      .catch(err => {
        console.error('[AuthContext] /admin/me failed:', err);
        localStorage.removeItem('adminToken');
        setAdmin(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (username, password) => {
    try {
      const res  = await fetch(`${API}/admin/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username, password }),
      });
      const data = await res.json();

      if (data.success && data.admin && data.token) {
        localStorage.setItem('adminToken', data.token);
        setAdmin(buildAdmin(data.admin, data.token));
        return true;
      }
      return false;
    } catch (err) {
      console.error('[AuthContext] login error:', err);
      return false;
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    setAdmin(null);
    window.location.href = '/admin/login';
  }, []);

  // ── Permission check ──────────────────────────────────────────────────────
  const hasPermission = useCallback((perm) => {
    if (!admin) return false;
    const perms = admin.permissions?.length
      ? admin.permissions
      : permissionsFor(admin.role || 'staff');
    return perms.includes(perm);
  }, [admin]);

  const isAdmin = admin?.role === 'admin';
  const isStaff = admin?.role === 'staff';

  return (
    <AuthContext.Provider value={{
      admin, loading, login, logout,
      hasPermission, isAdmin, isStaff,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);